import { Spin } from 'antd'
import AssetsIcon from 'assets/assets-tab-icon.svg?react'
import { AddButton } from 'components/CantileverButton'
import { ErrorScreen } from 'components/ErrorScreen'
import { PageHeader } from 'components/PageHeader'
import { DrawerState } from 'components/rightDrawer/utils/enums'
import { useRightDrawer } from 'components/rightDrawer/utils/RightDrawerProvider'
import { useAssetCardsData } from 'features/assetDatabase/hooks/useAssetCardsData'
import { useLandingPageData } from 'features/assetDatabase/hooks/useLandingPageData'
import {
  AssetData,
  AssetTableEntries,
  UnitAssetModelTable,
  UnitInspections,
} from 'features/assetDatabase/types/assetTableTypes'
import { AssetOverviewAdminGate } from 'features/auth'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { GeckoSite } from 'types'

import { AssetTable } from './AssetTable'
import {
  assetInspections,
  createAssetTableData,
  getAssetModels,
} from './AssetTableHelpers'

interface AssetTableDataProps {
  site: GeckoSite | undefined
  siteID: string
  onTableStateChange?: (newState: {
    asset?: string
    lastInspection?: string
    lastUpdated?: string
    components?: string[]
    area?: string
    areaDescription?: string
    erpId?: string
    criticality?: string
  }) => void
  initialTableState?: {
    asset?: string
    lastInspection?: string
    lastUpdated?: string
    components?: string[]
    area?: string
    areaDescription?: string
    erpId?: string
    criticality?: string
  }
}

const StyledAssetsIcon = styled(AssetsIcon)`
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.25rem;

  * {
    fill: ${({ theme }) => theme.forecast.defaultText};
  }
`
const AssetTableData: React.FC<AssetTableDataProps> = (props) => {
  const { siteID, onTableStateChange, initialTableState } = props
  const { setDrawerState, setDrawerEntry, setOnDrawerClose } = useRightDrawer()

  const {
    assetModels,
    units,
    components,
    componentDataFrames,
    componentDefaults,
    isLoading: isLoadingPage,
  } = useLandingPageData({ siteID })
  const {
    data: assetData,
    isLoading: isLoadingData,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<AssetTableEntries[]>({
    type: [
      'unit',
      'asset-model',
      'component',
      'inspection',
      'component-defaults',
    ],
    tags: { site: siteID },
    order_by: [{ key: 'last_updated', descending: 'true' }],
    limit: 1000000,
  })

  const cardProps = useAssetCardsData(
    assetModels,
    units,
    components,
    componentDataFrames,
    componentDefaults
  )

  const unitAssets = assetData
    ?.filter((asset) => asset.entry.type === 'unit')
    .map((unit) => unit.entry)

  const unitComponents = assetData
    ?.filter((asset) => asset.entry.type === 'component')
    .map((unit) => unit.entry.tags.unit)

  const unitAssetModels: UnitAssetModelTable[] | undefined =
    getAssetModels(assetData)

  const unitInspections: UnitInspections[] | undefined =
    assetInspections(assetData)

  const data: AssetData[] = useMemo(() => {
    return unitAssets && unitAssetModels && unitComponents && unitInspections
      ? createAssetTableData(
          unitAssets,
          unitAssetModels,
          unitComponents,
          unitInspections,
          cardProps
        )
      : []
  }, [unitAssets, unitAssetModels, unitComponents, unitInspections, cardProps])
  const errorScreenMessage =
    'Please contact support for the proper permissions.'
  return (
    <>
      <PageHeader
        title='Assets'
        icon={<StyledAssetsIcon />}
        trailingContent={
          <AssetOverviewAdminGate>
            <AddButton
              entityName='Asset'
              onClick={() => {
                if (siteID) {
                  setDrawerEntry(props.site)
                  setDrawerState(DrawerState.EditGeckoUnit)
                  setOnDrawerClose(() => mutate)
                }
              }}
            />
          </AssetOverviewAdminGate>
        }></PageHeader>
      <>
        {isLoadingData || isLoadingPage ? (
          <Spin spinning={true} />
        ) : !siteID ? (
          <ErrorScreen
            title={'Site was not properly loaded'}
            message={errorScreenMessage}
          />
        ) : data && data.length > 0 ? (
          <AssetTable
            siteID={siteID}
            isLoading={isLoadingData}
            data={data}
            onTableStateChange={onTableStateChange}
            initialTableState={initialTableState}
          />
        ) : (
          <ErrorScreen
            title={'No Assets for the Site'}
            message={errorScreenMessage}
          />
        )}
      </>
    </>
  )
}

export default AssetTableData
